<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="page-title-box">
          <h4 class="page-title">Assessment Result</h4>
        </div>
      </div>
    </div>
    <!-- end page title -->
    <div class="row">
      <div class="col-lg-12">
        <div class="card">
          <div class="card-body">
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-3">
                <label>Module</label>
                <v-select :options="modules" label="name" :reduce="m => m.id" v-model="filters.module_id"
                  placeholder="Select Module" />
              </div>

              <div class="col-md-3">
                <label>Trainer</label>
                <v-select
                  :options="trainers"         
                  label="name"                
                  :reduce="t => t.employee_id" 
                  v-model="filters.trainer_id"
                  placeholder="All Trainers"
                />
              </div>

              <div class="col-md-3">
                <label>Training Date</label>
                <input type="date" class="form-control" v-model="filters.from_date" />
              </div>

              <div class="col-md-3 d-flex align-items-end gap-2">
                 <div @click="resetFilter" class="icon-css mt-3">
                  <i style="color: #4a81d4" class="fe-rotate-cw"></i>
                </div>
              </div>
            </div>
            <div class="table-responsive">
  <table class="table table-striped dt-responsive w-100 mb-3" id="datatable" v-show="!loading">
    <thead>
      <tr>
        <th>No.</th>
        <th>Employee Name</th>
        <th>Business Unit</th>
        <th>Module</th>
        <th>Trainer Name</th>
        <th>Training Date</th>
        <th>Expire Date</th>
        <th>Status</th>
        <th>Submitted Date</th>
        <th>Total Score</th>
        <th>Action</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="(data, index) in assessment_timeline" :key="data.assessment_id || index">
        <td>{{ index + 1 }}</td>
        <td>{{ data.employee_name || '-' }}</td>
        <td>{{ data.business_unit }}</td>
        <td>{{ data.module_name || '-' }}</td>
        <td>{{ data.trainer }}</td>
        <td>{{ data.from_date }}</td>
        <td>{{ data.expire_date }}</td>
        <td>{{ getStatusText(data.status) }}</td>
        <td>{{ getDateOnly(data.submitted_date) }}</td>
        <td>{{ data.total }}</td>
        <td>
          <router-link
            v-if="data.assessment_id && hasPermissions(['self-assessment-result-edit'])"
            class="action-icon"
            :to="{ name: 'after-month-assessment-result/edit', params: { id: data.assessment_id } }"
          >
            <i class="mdi mdi-square-edit-outline"></i>
          </router-link>
          <span v-else class="text-muted">-</span>
        </td>
      </tr>

      <!-- Empty state -->
      <tr v-if="assessment_timeline.length === 0">
        <td colspan="11" class="text-center py-4 text-muted">
          No assessment timeline data available
        </td>
      </tr>
    </tbody>
  </table>
</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "datatables.net/js/jquery.dataTables.min.js";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import "datatables.net-bs5/js/dataTables.bootstrap5.min.js";
import "datatables.net-responsive-bs5/css/responsive.bootstrap5.min.css";
import "datatables.net-responsive-bs5/js/responsive.bootstrap5.min.js";
import $ from "jquery";
import axios from "axios";
import { useToast } from "vue-toastification";
import badgeSuccess from "../../../components/shared/badgeSuccess.vue";
import badgeDanger from "../../../components/shared/badgeDanger.vue";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import userHasPermissions from "../../../mixins/userHasPermissions";

export default {
  name: "AssessmentTimeline",
  components: {
    badgeSuccess,
    badgeDanger,
    vSelect,
  },
  mixins: [userHasPermissions],
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      employees: [],
      assessment_timeline: [],
      modules: [],
      trainers: [],
      loading: false,
      baseUrl: process.env.VUE_APP_BASE_URL,
      dataTableInstance: null,
      filters: {
        module_id: null,
        from_date: null,
        trainer_id: null,
      },
    };
  },
  methods: {
    getStatusText(status) {
      switch (parseInt(status)) {
        case 0: return "Waiting";
        case 1: return "On Progress";
        case 2: return "Complete";
        default: return "Unknown";
      }
    },
    async getTrainers() {
      try {
        const res = await axios.get(`${this.baseUrl}admin/v3/trainer-list`);
        this.trainers = res.data.data || [];
        
      } catch (e) { 
        console.error("Failed to load trainers", e);
      }
    },
    getDateOnly(timestamp) {
      if (!timestamp) return "";
      const date = new Date(timestamp);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    async getModules() {
      try {
        const res = await axios.get(`${this.baseUrl}admin/v3/module-list`);
        this.modules = res.data.data || [];
      } catch (e) {
        console.error("Failed to load modules", e);
      }
    },
    async getAssessmentResult() {
      this.loading = true;

      try {
        const params = {};
        if (this.filters.module_id) params.module_id = this.filters.module_id;
        if (this.filters.from_date) params.from_date = this.filters.from_date;
        if (this.filters.trainer_id) params.trainer_id = this.filters.trainer_id;

        // console.log("Fetching assessment timeline with params:", params);

        const response = await axios.get(
          `${this.baseUrl}admin/v3/self-assessment-results`,
          { params }
        );

        this.assessment_timeline = response.data.data || [];

        // Destroy existing DataTable before re-initializing
        if (this.dataTableInstance) {
          this.dataTableInstance.destroy();
          this.dataTableInstance = null;
        }

        this.$nextTick(() => {
          if (this.assessment_timeline.length) {
            this.initializeDataTable();
          }
        });
      } catch (error) {
        console.error(error);
        this.toast.error("Failed to load results");
      } finally {
        this.loading = false;
      }
    },
    initializeDataTable() {
      this.dataTableInstance = $("#datatable").DataTable({
        responsive: true,
        order: [[0, "asc"]],
        pageLength: 10, // Default rows per page
        lengthMenu: [
          [10, 25, 50, -1],
          [10, 25, 50, "All"],
        ],
        language: {
          search: "Search:",
          lengthMenu: "Show _MENU_ entries",
          info: "Showing _START_ to _END_ of _TOTAL_ entries",
          infoEmpty: "Showing 0 to 0 of 0 entries",
          infoFiltered: "(filtered from _MAX_ total entries)",
          paginate: {
            first: "First",
            last: "Last",
            next: "Next",
            previous: "Previous",
          },
        },
      });
    },
    applyFilter() {
      this.getAssessmentResult();
    },
    resetFilter() {
      this.filters.module_id = null;
      this.filters.from_date = null;
      this.filters.trainer_id = null;
      this.getAssessmentResult();
    },
  },
  created() {
    this.getModules();
    this.getAssessmentResult();
    this.getTrainers();

    // Load employees asynchronously from store
    let intervalId;
    if (!this.employees.length) {
      intervalId = setInterval(() => {
        this.employees = this.$store.getters["odoo/getAllEmployees"] || [];
        if (this.employees.length) clearInterval(intervalId);
      }, 1000);
    }
  },
  watch: {
    "filters.module_id"(newVal) {
      this.getAssessmentResult();
    },
    "filters.trainer_id"(newVal) {
      this.getAssessmentResult();
    },
    "filters.from_date"(newVal) {
      this.getAssessmentResult();
    }
  },
  beforeUnmount() {
    // Destroy DataTable instance to prevent memory leaks
    if (this.dataTableInstance) {
      this.dataTableInstance.destroy();
    }
  },
};
</script>

<style scoped>
.page-item.active .page-link {
  z-index: 3;
  color: #fff;
  background-color: #4a81d4;
  border-color: #4a81d4;
}

.action-icon {
  cursor: pointer;
  font-size: 18px;
  text-decoration: none;
}

.action-icon:hover {
  opacity: 0.8;
}

.badge {
  padding: 5px 10px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
}

.badge-warning {
  background-color: #ffc107;
  color: #212529;
}

.badge-info {
  background-color: #17a2b8;
  color: white;
}

.badge-success {
  background-color: #1abc9c;
  color: white;
}

.badge-secondary {
  background-color: #6c757d;
  color: white;
}

.text-muted {
  color: #6c757d !important;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}
</style>